import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

// ── Progress Bar ───────────────────────────────────────────────────────────────
function ProgressBar({ pct }) {
    const capped = Math.min(pct, 100);
    const label = pct > 100 ? '100+%' : `${pct}%`;
    return (
        <div style={styles.progressWrap}>
            <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${capped}%` }} />
            </div>
            <span style={styles.progressLabel}>{label}</span>
        </div>
    );
}

// ── @mention parser ────────────────────────────────────────────────────────────
function MentionText({ text }) {
    if (!text) return null;
    const parts = text.split(/(@\w+)/g);
    return (
        <span>
            {parts.map((part, i) =>
                /^@\w+$/.test(part)
                    ? <span key={i} style={styles.mention}>{part}</span>
                    : part
            )}
        </span>
    );
}

// ── Reply Item ─────────────────────────────────────────────────────────────────
function ReplyItem({ reply, currentUserId, onEdit, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState(reply.body);
    const [saving, setSaving] = useState(false);

    const formatTs = (ts) => ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

    const handleSave = async () => {
        if (!editBody.trim()) return;
        setSaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.put(`${config.API_URL}/projects/replies/${reply.id}`, { body: editBody.trim() }, { headers: { Authorization: token } });
            onEdit(reply.id, res.data.body);
            setEditing(false);
        } catch (e) {
            alert('Failed to update reply.');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this reply?')) return;
        try {
            const token = await getIdToken();
            await axios.put(`${config.API_URL}/projects/replies/${reply.id}`, { is_deleted: true }, { headers: { Authorization: token } });
            onDelete(reply.id);
        } catch (e) { alert('Failed to delete reply.'); }
    };

    const isOwn = currentUserId && reply.author_id === currentUserId;

    return (
        <div style={styles.replyItem}>
            <div style={styles.postMeta}>
                <span style={styles.authorName}>{reply.author_display_name || 'Unknown'}</span>
                <span style={styles.postTime}>{formatTs(reply.created_at)}</span>
                {reply.updated_at !== reply.created_at && !reply.is_deleted && <span style={styles.editedTag}>(edited)</span>}
            </div>
            {reply.is_deleted ? (
                <div style={styles.deletedText}>[message deleted]</div>
            ) : editing ? (
                <div style={styles.composerWrap}>
                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)} style={{ ...styles.textarea, minHeight: '60px' }} />
                    <div style={styles.composerActions}>
                        <button onClick={handleSave} disabled={saving} style={styles.postBtn}>{saving ? 'Saving...' : 'Save'}</button>
                        <button onClick={() => { setEditing(false); setEditBody(reply.body); }} style={styles.cancelBtn}>Cancel</button>
                    </div>
                </div>
            ) : (
                <>
                    <div style={styles.postBody}><MentionText text={reply.body} /></div>
                    {isOwn && (
                        <div style={styles.postActions}>
                            <button onClick={() => setEditing(true)} style={styles.actionBtn}>Edit</button>
                            <button onClick={handleDelete} style={styles.actionBtn}>Delete</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ── Post Item ──────────────────────────────────────────────────────────────────
function PostItem({ post, currentUserId, onEditPost, onDeletePost, onAddReply, onEditReply, onDeleteReply }) {
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState(post.body);
    const [saving, setSaving] = useState(false);
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [replySaving, setReplySaving] = useState(false);

    const formatTs = (ts) => ts ? new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

    const handleSave = async () => {
        if (!editBody.trim()) return;
        setSaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.put(`${config.API_URL}/projects/posts/${post.id}`, { body: editBody.trim() }, { headers: { Authorization: token } });
            onEditPost(post.id, res.data.body);
            setEditing(false);
        } catch (e) { alert('Failed to update post.'); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this post?')) return;
        try {
            const token = await getIdToken();
            await axios.put(`${config.API_URL}/projects/posts/${post.id}`, { is_deleted: true }, { headers: { Authorization: token } });
            onDeletePost(post.id);
        } catch (e) { alert('Failed to delete post.'); }
    };

    const handleReply = async () => {
        if (!replyBody.trim()) return;
        setReplySaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.post(`${config.API_URL}/projects/${post.project_id}/posts/${post.id}/replies`, { body: replyBody.trim() }, { headers: { Authorization: token } });
            onAddReply(post.id, res.data);
            setReplyBody('');
            setReplyOpen(false);
        } catch (e) { alert('Failed to post reply.'); }
        finally { setReplySaving(false); }
    };

    const isOwn = currentUserId && post.author_id === currentUserId;

    return (
        <div style={styles.postItem}>
            <div style={styles.postMeta}>
                <span style={styles.authorName}>{post.author_display_name || 'Unknown'}</span>
                <span style={styles.postTime}>{formatTs(post.created_at)}</span>
                {post.updated_at !== post.created_at && !post.is_deleted && <span style={styles.editedTag}>(edited)</span>}
            </div>

            {post.is_deleted ? (
                <div style={styles.deletedText}>[message deleted]</div>
            ) : editing ? (
                <div style={styles.composerWrap}>
                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)} style={styles.textarea} />
                    <div style={styles.composerActions}>
                        <button onClick={handleSave} disabled={saving} style={styles.postBtn}>{saving ? 'Saving...' : 'Save'}</button>
                        <button onClick={() => { setEditing(false); setEditBody(post.body); }} style={styles.cancelBtn}>Cancel</button>
                    </div>
                </div>
            ) : (
                <>
                    <div style={styles.postBody}><MentionText text={post.body} /></div>
                    <div style={styles.postActions}>
                        {currentUserId && !post.is_deleted && (
                            <button onClick={() => setReplyOpen(r => !r)} style={styles.replyBtn}>↩ Reply</button>
                        )}
                        {isOwn && (
                            <>
                                <button onClick={() => setEditing(true)} style={styles.actionBtn}>Edit</button>
                                <button onClick={handleDelete} style={styles.actionBtn}>Delete</button>
                            </>
                        )}
                    </div>
                </>
            )}

            {/* Replies */}
            {post.replies && post.replies.length > 0 && (
                <div style={styles.repliesWrap}>
                    {post.replies.map(r => (
                        <ReplyItem
                            key={r.id}
                            reply={r}
                            currentUserId={currentUserId}
                            onEdit={(rid, body) => onEditReply(post.id, rid, body)}
                            onDelete={(rid) => onDeleteReply(post.id, rid)}
                        />
                    ))}
                </div>
            )}

            {/* Reply composer */}
            {replyOpen && (
                <div style={{ ...styles.composerWrap, marginTop: '12px', marginLeft: '24px' }}>
                    <textarea
                        value={replyBody}
                        onChange={e => setReplyBody(e.target.value)}
                        placeholder="Write a reply... use @username to mention someone"
                        style={{ ...styles.textarea, minHeight: '60px' }}
                    />
                    <div style={styles.composerActions}>
                        <button onClick={handleReply} disabled={replySaving} style={styles.postBtn}>{replySaving ? 'Posting...' : 'Reply'}</button>
                        <button onClick={() => { setReplyOpen(false); setReplyBody(''); }} style={styles.cancelBtn}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────────
function ProjectDetail() {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [followLoading, setFollowLoading] = useState(false);
    const [newPostBody, setNewPostBody] = useState('');
    const [postSaving, setPostSaving] = useState(false);

    // Get current user
    useEffect(() => {
        getIdToken().then(token => {
            if (token) {
                // Decode JWT sub
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    setCurrentUserId(payload.sub);
                } catch { }
            }
        }).catch(() => { });
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        axios.get(`${config.API_URL}/projects/${id}`)
            .then(res => {
                setProject(res.data);
                document.title = `SHARC - ${res.data.name}`;
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load project.');
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [load]);

    const entityPath = () => {
        if (!project) return null;
        const map = { artist: 'artists', venue: 'venues', promoter: 'promoters', member: 'members' };
        return `/${map[project.entity_type]}/${project.entity_slug || project.entity_id}`;
    };

    const handleFollow = async () => {
        if (!currentUserId) { alert('Please sign in to follow projects.'); return; }
        setFollowLoading(true);
        try {
            const token = await getIdToken();
            const method = project.is_following ? 'delete' : 'post';
            await axios[method](`${config.API_URL}/projects/${id}/follow`, {}, { headers: { Authorization: token } });
            setProject(prev => ({
                ...prev,
                is_following: !prev.is_following,
                follower_count: prev.is_following ? prev.follower_count - 1 : prev.follower_count + 1,
            }));
        } catch (e) { alert('Failed to update follow status.'); }
        finally { setFollowLoading(false); }
    };

    const handleNewPost = async () => {
        if (!newPostBody.trim()) return;
        setPostSaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.post(`${config.API_URL}/projects/${id}/posts`, { body: newPostBody.trim() }, { headers: { Authorization: token } });
            setProject(prev => ({ ...prev, posts: [...(prev.posts || []), res.data] }));
            setNewPostBody('');
        } catch (e) { alert('Failed to post.'); }
        finally { setPostSaving(false); }
    };

    // Forum mutation handlers
    const onEditPost = (postId, body) => {
        setProject(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, body } : p) }));
    };
    const onDeletePost = (postId) => {
        setProject(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, is_deleted: true } : p) }));
    };
    const onAddReply = (postId, reply) => {
        setProject(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: [...(p.replies || []), reply] } : p) }));
    };
    const onEditReply = (postId, replyId, body) => {
        setProject(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: p.replies.map(r => r.id === replyId ? { ...r, body } : r) } : p) }));
    };
    const onDeleteReply = (postId, replyId) => {
        setProject(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: p.replies.map(r => r.id === replyId ? { ...r, is_deleted: true } : r) } : p) }));
    };

    if (loading) return <div style={styles.message}>Loading project...</div>;
    if (error) return <div style={styles.message}>{error}</div>;
    if (!project) return null;

    const ePath = entityPath();
    const typeLabel = project.entity_type.charAt(0).toUpperCase() + project.entity_type.slice(1);

    return (
        <div>
            <Link to="/projects" style={styles.backLink}>← Back to Projects</Link>

            {/* Section 1: Header */}
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <h1 style={styles.title}>{project.name}</h1>
                    <div style={styles.headerButtons}>
                        <Link to={`/projects/${id}/edit`} style={styles.editButton}>Edit Project</Link>
                        <button
                            onClick={handleFollow}
                            disabled={followLoading}
                            style={project.is_following ? styles.unfollowButton : styles.followButton}
                        >
                            {followLoading ? '...' : project.is_following ? 'Unfollow Project' : 'Follow Project'}
                        </button>
                    </div>
                </div>
                <div style={styles.followerCount}>{project.follower_count} {project.follower_count === 1 ? 'follower' : 'followers'}</div>
            </div>

            {/* Section 2: Details */}
            <div style={styles.detailsCard}>
                <table style={styles.detailsTable}>
                    <tbody>
                        <tr>
                            <td style={styles.detailLabel}>Entity Type</td>
                            <td style={styles.detailValue}>{typeLabel}</td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Entity</td>
                            <td style={styles.detailValue}>
                                {ePath ? <Link to={ePath} style={styles.link}>{project.entity_name}</Link> : project.entity_name}
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Metric</td>
                            <td style={styles.detailValue}>{project.metric}</td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Date Range</td>
                            <td style={styles.detailValue}>{project.date_start} to {project.date_end}</td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Estimated Shows</td>
                            <td style={styles.detailValue}>{project.estimated_value}</td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Shows Logged</td>
                            <td style={styles.detailValue}>{project.actual_value}</td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Progress</td>
                            <td style={{ ...styles.detailValue, minWidth: '240px' }}>
                                <ProgressBar pct={project.pct_complete} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.detailLabel}>Created By</td>
                            <td style={styles.detailValue}>{project.created_by_display_name || '—'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Section 3: Forum */}
            <div style={styles.forumSection}>
                <h2 style={styles.forumTitle}>Discussion</h2>

                {/* New post composer */}
                {currentUserId ? (
                    <div style={styles.composerCard}>
                        <textarea
                            value={newPostBody}
                            onChange={e => setNewPostBody(e.target.value)}
                            placeholder="Start a discussion... use @username to mention someone"
                            style={styles.textarea}
                        />
                        <div style={styles.composerActions}>
                            <button onClick={handleNewPost} disabled={postSaving || !newPostBody.trim()} style={styles.postBtn}>
                                {postSaving ? 'Posting...' : 'Post'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={styles.signInPrompt}>
                        Sign in to join the discussion.
                    </div>
                )}

                {/* Posts */}
                {!project.posts || project.posts.length === 0 ? (
                    <div style={styles.emptyForum}>No posts yet. Start the discussion!</div>
                ) : (
                    <div style={styles.postsList}>
                        {project.posts.map(post => (
                            <PostItem
                                key={post.id}
                                post={{ ...post, project_id: id }}
                                currentUserId={currentUserId}
                                onEditPost={onEditPost}
                                onDeletePost={onDeletePost}
                                onAddReply={onAddReply}
                                onEditReply={onEditReply}
                                onDeleteReply={onDeleteReply}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    header: { marginBottom: '32px' },
    headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' },
    title: { fontSize: '36px', fontWeight: 'bold', color: '#e0e0e0', margin: '0 0 8px 0', flex: 1 },
    headerButtons: { display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '6px', padding: '8px 18px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold', whiteSpace: 'nowrap' },
    followButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '8px 18px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },
    unfollowButton: { backgroundColor: 'transparent', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '6px', padding: '8px 18px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },
    followerCount: { fontSize: '13px', color: '#666', marginTop: '4px' },
    detailsCard: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '24px', marginBottom: '32px' },
    detailsTable: { width: '100%', borderCollapse: 'collapse' },
    detailLabel: { padding: '10px 16px 10px 0', fontSize: '13px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', width: '160px', verticalAlign: 'middle' },
    detailValue: { padding: '10px 0', fontSize: '15px', color: '#e0e0e0', verticalAlign: 'middle' },
    link: { color: '#bb86fc', textDecoration: 'none' },
    progressWrap: { display: 'flex', alignItems: 'center', gap: '10px' },
    progressTrack: { flex: 1, height: '10px', backgroundColor: '#2c2c2c', borderRadius: '5px', overflow: 'hidden', maxWidth: '260px' },
    progressFill: { height: '100%', backgroundColor: '#bb86fc', borderRadius: '5px', transition: 'width 0.3s ease' },
    progressLabel: { fontSize: '13px', color: '#bb86fc', fontWeight: 'bold', whiteSpace: 'nowrap' },
    forumSection: { marginTop: '8px' },
    forumTitle: { fontSize: '22px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '20px' },
    composerCard: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '16px', marginBottom: '24px' },
    composerWrap: { marginTop: '8px' },
    textarea: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '12px', fontSize: '14px', color: '#e0e0e0', width: '100%', minHeight: '80px', resize: 'vertical', lineHeight: '1.5', boxSizing: 'border-box' },
    composerActions: { display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' },
    postBtn: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '8px 20px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' },
    cancelBtn: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px', padding: '8px 16px', fontSize: '14px', cursor: 'pointer' },
    signInPrompt: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '24px' },
    emptyForum: { textAlign: 'center', color: '#555', padding: '40px 0', fontSize: '15px' },
    postsList: { display: 'flex', flexDirection: 'column', gap: '16px' },
    postItem: { backgroundColor: '#1e1e1e', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '16px' },
    postMeta: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
    authorName: { fontWeight: 'bold', color: '#bb86fc', fontSize: '14px' },
    postTime: { fontSize: '12px', color: '#555' },
    editedTag: { fontSize: '12px', color: '#444', fontStyle: 'italic' },
    postBody: { fontSize: '14px', color: '#ddd', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    deletedText: { fontSize: '14px', color: '#444', fontStyle: 'italic' },
    postActions: { display: 'flex', gap: '12px', marginTop: '10px' },
    replyBtn: { background: 'none', border: 'none', color: '#777', fontSize: '13px', cursor: 'pointer', padding: 0 },
    actionBtn: { background: 'none', border: 'none', color: '#555', fontSize: '13px', cursor: 'pointer', padding: 0 },
    repliesWrap: { marginTop: '12px', marginLeft: '24px', borderLeft: '2px solid #2a2a2a', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
    replyItem: { backgroundColor: '#181818', borderRadius: '6px', padding: '12px' },
    mention: { color: '#bb86fc', fontWeight: '600' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default ProjectDetail;
